import React, { useState } from 'react';
import {
  Button,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import {
  Download as DownloadIcon,
  PictureAsPdf as PictureAsPdfIcon,
  Description as DescriptionIcon,
  TableChart as ExcelIcon,
} from '@mui/icons-material';

/**
 * Unified Export Button Component
 * 
 * @param {Object} props
 * @param {Function} props.onExportPdf - Function to handle PDF export
 * @param {Function} props.onExportCsv - Function to handle CSV export
 * @param {Function} props.onExportExcel - Function to handle Excel export (optional)
 * @param {string} props.label - Button label (default: 'Export')
 * @param {string} props.variant - Button variant (default: 'contained')
 * @param {Object} props.sx - Custom sx styles for the button
 */
const ExportButton = ({ 
  onExportPdf, 
  onExportCsv, 
  onExportExcel, 
  label = 'Export', 
  variant = 'contained',
  sx = {} 
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleMenuClick = (type) => {
    handleClose();
    if (type === 'pdf' && onExportPdf) onExportPdf();
    if (type === 'csv' && onExportCsv) onExportCsv();
    if (type === 'excel' && onExportExcel) onExportExcel();
  };

  return (
    <>
      <Button
        variant={variant}
        startIcon={<DownloadIcon />}
        onClick={handleClick}
        sx={{
          backgroundColor: 'primary.main',
          textTransform: 'none',
          fontWeight: 600,
          borderRadius: 2,
          color: 'primary.contrastText',
          '&:hover': {
            backgroundColor: 'primary.dark',
          },
          ...sx
        }}
      >
        {label}
      </Button>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        {onExportPdf && (
          <MenuItem onClick={() => handleMenuClick('pdf')}>
            <ListItemIcon>
              <PictureAsPdfIcon fontSize="small" color="error" />
            </ListItemIcon>
            <ListItemText primary="Export as PDF" />
          </MenuItem>
        )}
        {onExportCsv && (
          <MenuItem onClick={() => handleMenuClick('csv')}>
            <ListItemIcon>
              <DescriptionIcon fontSize="small" color="primary" />
            </ListItemIcon>
            <ListItemText primary="Export as CSV" />
          </MenuItem>
        )}
        {onExportExcel && (
          <MenuItem onClick={() => handleMenuClick('excel')}>
            <ListItemIcon>
              <ExcelIcon fontSize="small" color="success" />
            </ListItemIcon>
            <ListItemText primary="Export as Excel" />
          </MenuItem>
        )}
      </Menu>
    </>
  );
};

export default ExportButton;
